import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import { FaArrowLeft as FaArrowLeftRaw } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import ClassSectionSidebar from '../../components/classroom/ClassSectionSidebar';
import ClassDiscussionBoard from '../../components/classroom/ClassDiscussionBoard';
import { teacherService } from '../../services/classroom/teacherService';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Hero = styled.section`
  border-radius: 16px;
  border: 1px solid var(--outline);
  padding: 1.4rem 1.55rem 1.3rem;
  background: linear-gradient(
    135deg,
    var(--primaryDark) 0%,
    var(--primary) 55%,
    color-mix(in srgb, var(--primary) 70%, var(--surface)) 100%
  );
  color: var(--onPrimary);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.1);
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 2.7vw, 1.8rem);
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const HeroSub = styled.p`
  margin: 0;
  font-size: 0.87rem;
  opacity: 0.92;
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Hint = styled.p`
  margin: 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const MainColumn = styled.div`
  min-width: 0;
`;

const ClassroomTeacherClassDiscussionPage: React.FC = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const { user } = useAuth();

  const classQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'class', classId],
    queryFn: () => teacherService.getClassById(classId),
    enabled: Boolean(classId),
  });

  if (classQuery.isLoading) return <Hint>Loading class discussion...</Hint>;
  if (classQuery.isError) return <Hint>Unable to load class discussion.</Hint>;
  if (!classQuery.data) return <Hint>Class not found.</Hint>;

  const className = classQuery.data.name;
  const shortClassName = className.split(/\s+/).slice(0, 3).join(' ') || 'This class';

  return (
    <Page>
      <BackRow>
        <BackLink to={`/classroom-teacher/classes/${encodeURIComponent(classId)}`}>
          <FaArrowLeft size={12} aria-hidden />
          Back to Overview
        </BackLink>
      </BackRow>

      <Hero>
        <HeroTitle>{className} Discussion</HeroTitle>
        <HeroSub>Manage student conversations, replies, and moderation in one place.</HeroSub>
      </Hero>

      <LayoutGrid>
        <ClassSectionSidebar classId={classId} className={shortClassName} />
        <MainColumn>
          <ClassDiscussionBoard classId={classId} currentUserId={user?.id ?? null} />
        </MainColumn>
      </LayoutGrid>
    </Page>
  );
};

export default ClassroomTeacherClassDiscussionPage;
